import { Ref, watchEffect } from 'vue';
import { Allocation } from '../Timeline.types';

/**
 * Minimal location shape the validator depends on (id + children). Narrowed — like useConflictMarkers'
 * AllocationDateRange — so every caller's location tree satisfies it without conversion. `nestedLocations`
 * is optional because external-event locations are a flat list with no nesting at all.
 */
type LocationTreeNode = {
    id: string;
    nestedLocations?: LocationTreeNode[];
};

function collectLocationIds(locations: LocationTreeNode[], counts: Map<string, number>): void {
    for (const location of locations) {
        counts.set(location.id, (counts.get(location.id) ?? 0) + 1);
        if (location.nestedLocations && location.nestedLocations.length > 0) {
            collectLocationIds(location.nestedLocations, counts);
        }
    }
}

/**
 * Throws on corrupt timeline input. Runs in production (not dev-only) — corrupt data should block
 * rendering loudly rather than surface as silently-missing rows.
 *
 * Checks:
 *  1. Location ids are unique across the whole tree.
 *  2. Allocation ids are unique.
 *  3. Every allocation has a valid (start < end) date range.
 *  4. Every allocation has at least one phase.
 *  5. Every allocation's phases exactly span its date range (Σ cellSpan === days between start and end).
 *
 * Allocations whose location is not part of the tree are skipped, not rejected: the timeline fetches
 * only the location types in TIMELINE_ROOM_TYPES / TIMELINE_AREA_TYPES, so allocations on a type it
 * no longer shows (e.g. "Konferenzraum") legitimately have no row.
 */
export function validateTimelineRows(locations: LocationTreeNode[], allocations: Allocation[]): void {
    // Nothing to validate before locations have loaded — avoids false positives on transient empty state.
    if (locations.length === 0) {
        return;
    }

    // 1. Unique location ids
    const locationIdCounts = new Map<string, number>();
    collectLocationIds(locations, locationIdCounts);
    const duplicateLocationIds = [...locationIdCounts].filter(([, count]) => count > 1).map(([id]) => id);
    if (duplicateLocationIds.length > 0) {
        throw new Error(`Timeline: duplicate location id(s): ${duplicateLocationIds.join(', ')}`);
    }

    // 2. Unique allocation ids
    const seenAllocationIds = new Set<string>();
    for (const allocation of allocations) {
        if (seenAllocationIds.has(allocation.id)) {
            throw new Error(`Timeline: duplicate allocation id "${allocation.id}"`);
        }
        seenAllocationIds.add(allocation.id);
    }

    for (const allocation of allocations) {
        // Allocation on a location type the timeline does not display — nothing to render, nothing to check.
        if (!locationIdCounts.has(allocation.locationId)) {
            continue;
        }

        // 3. Valid date range
        if (allocation.startDateTime >= allocation.endDateTime) {
            throw new Error(
                `Timeline: allocation "${allocation.id}" has an invalid date range (start ${allocation.startDateTime.toISO()} is not before end ${allocation.endDateTime.toISO()})`,
            );
        }

        // 4. At least one phase
        if (allocation.phases.length === 0) {
            throw new Error(`Timeline: allocation "${allocation.id}" has no phases`);
        }

        // 5. Phases exactly span the allocation's date range. Checked on raw cellSpan (no enrichment):
        // Σ cellSpan must equal the number of days between start and end.
        const totalCellSpan = allocation.phases.reduce((sum, phase) => sum + phase.cellSpan, 0);
        const spanInDays = allocation.endDateTime.diff(allocation.startDateTime, 'days').days;
        if (totalCellSpan !== spanInDays) {
            throw new Error(
                `Timeline: allocation "${allocation.id}" phase span (${totalCellSpan}d) does not match its ` +
                    `date range (${spanInDays}d: ${allocation.startDateTime.toISO()} → ${allocation.endDateTime.toISO()})`,
            );
        }
    }
}

/**
 * Reactively validates the timeline's locations + allocations, throwing on corrupt data.
 * Intended to be called once per timeline consumer with the built location tree and the raw allocations.
 */
export function useTimelineValidation(locations: Ref<LocationTreeNode[]>, allocations: Ref<Allocation[]>): void {
    watchEffect(() => {
        let currentLocations: LocationTreeNode[];
        let currentAllocations: Allocation[];
        try {
            // Reading these can throw while the underlying data query is still loading — some data
            // composables throw (e.g. "Missing site") from a computed until the query resolves.
            // That's a transient not-ready state, so skip validation until the data is available.
            currentLocations = locations.value;
            currentAllocations = allocations.value;
        } catch {
            return;
        }
        // Deliberately outside the try: genuinely corrupt data must still throw (see validateTimelineRows).
        validateTimelineRows(currentLocations, currentAllocations);
    });
}
